import { Component, HostListener, inject, Inject, OnInit } from '@angular/core';
import Auth from '@aws-amplify/auth';
import { AuthenticationService } from '../../service/authentication.service';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UtilsService } from '../../service/utils.service';
import { ToastrService } from 'ngx-toastr';
import { LoginEvent } from '../../model/LoginEvent';
import { DOCUMENT } from '@angular/common';
import { Role } from '../../model/Role';
import { UserState } from '../../store/User/UserReducer';
import { Store } from '@ngrx/store';
import { InitUser } from '../../store/User/UserActions';
import { SocketService } from '../../service/socket.service';

@Component({
  selector: 'cw-login',
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  scrollClass = false;
  form: FormGroup;

  isLoginInProgress: boolean;

  event: string = LoginEvent.LOGIN;

  loginEvent = LoginEvent;

  @HostListener('window:scroll', ['$event'])
  handleScroll() {
    this.scrollClass = window.scrollY > 30;
  }

  constructor(
    private readonly authService: AuthenticationService,
    private readonly router: Router,
    private readonly userStore: Store<UserState>,
    private readonly socketService: SocketService,
    private readonly toastrService: ToastrService,
    private readonly fb: FormBuilder,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    this.form = this.fb.group(
      {
        username: new FormControl('', [Validators.required]),
        code: new FormControl({ value: '', disabled: true }, [
          Validators.required,
        ]),
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(200),
        ]),
        newPassword: new FormControl(
          {
            value: '',
            disabled: true,
          },
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(200),
          ],
        ),
        newPasswordAgain: new FormControl(
          {
            value: '',
            disabled: true,
          },
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(200),
          ],
        ),
      },
      { validators: [UtilsService.passwordsMatched()] },
    );
  }

  changeToForgotPassword() {
    this.event = LoginEvent.FORGOT_PASSWORD;
  }

  backToLogin() {
    this.event = LoginEvent.LOGIN;
  }

  forgetPassword() {
    if (this.form.get('username').invalid) {
      return;
    }

    Auth.forgotPassword(this.form.get('username').value)
      .then((data) => {
        this.event = LoginEvent.FORGOT_PASSWORD_SUBMIT;
        this.form.get('code').enable();
      })
      .catch((err) => console.log(err));
  }

  forgetPasswordSubmit() {
    const username = this.form.get('username').value;
    const code = this.form.get('code').value;
    const password = this.form.get('password').value;
    Auth.forgotPasswordSubmit(username, code, password)
      .then((data) => {
        this.event = LoginEvent.LOGIN;
        this.toastrService.success('Password successfully changed');
        this.form.reset();
      })
      .catch((err) => console.log(err));
  }

  async jLogin() {
    if (
      this.isLoginInProgress ||
      this.form.get('username').invalid ||
      this.form.get('password').invalid
    ) {
      return;
    }

    this.isLoginInProgress = true;
    const username = this.form.value.username;
    const password = this.form.value.password;

    const csrf = await this.authService.fetchCsrfToken();
    if (!csrf) {
      this.isLoginInProgress = false;
      this.toastrService.error('Failed to retrieve CSRF token.');
      return;
    }

    try {
      const loginResp = await this.authService.jLoginPromise(
        username,
        password,
        csrf.token,
      );
      console.log('Login Response:', loginResp);

      if (loginResp.url.includes('error')) {
        this.isLoginInProgress = false;
        this.form.get('password').reset();
        this.toastrService.error('Invalid username or password');
        return;
      }

      this.authService.userInfo().subscribe({
        next: (resp: any) => {
          const user = resp.userinfo;
          this.userStore.dispatch(new InitUser(user, null));
          this.isLoginInProgress = false;

          if (
            user.userType === Role.ADMINISTRATOR ||
            user.userType === Role.GROUP
          ) {
            this.router.navigateByUrl('/account-selection');
          } else {
            this.router.navigateByUrl('/report');
          }
        },
        error: (err) => {
          console.error('User Info Error:', err);
          this.isLoginInProgress = false;
          this.toastrService.error('Failed to fetch user info');
        },
      });
    } catch (e) {
      console.error('Login Error:', e);
      this.isLoginInProgress = false;
      if (e.status === 403) {
        this.toastrService.error(
          'Invalid CSRF token or session. Please try again.',
        );
      } else if (e.status === 401) {
        this.toastrService.error('Invalid username or password');
      } else {
        this.toastrService.error('Login failed. Please try again.');
      }
    }
  }
}
